import React, { useEffect, useRef, useState } from 'react'

import { Button } from '../../New/Button/Button'
import { Close } from '@carbon/icons-react'
import { DDSTypography } from 'den-design-system'
import Input from '../../New/Input/Input'
import RegexConstants from '../../../constants/RegexConstants'
import pallete from '../../../global/newPallete'

interface CustomLinkEditorProps {
  initialUrl: string
  initialText: string
  onUpdate: (newText: string, newUrl: string) => void
  onRemove: () => void
  onClose: () => void
  position: { top: number; left: number }
  variant?: 'create' | 'update'
}

const CustomLinkEditor: React.FC<CustomLinkEditorProps> = ({
  initialUrl,
  initialText,
  onUpdate,
  onRemove,
  onClose,
  position,
  variant
}) => {
  const cardRef = useRef<HTMLDivElement>(null)

  const [url, setUrl] = useState(initialUrl)
  const [text, setText] = useState(initialText)
  const [isEditing, setIsEditing] = useState(false)
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)

  const handleCancel = () => {
    setText(initialText)
    setUrl(initialUrl)
    setIsEditing(false)
  }

  const handleApply = () => {
    onUpdate(text, url)
    setIsEditing(false)
  }

  const handleRemove = () => {
    setText('')
    setUrl('')
    onRemove()
    onClose()
  }
  const verticalOffset = 300
  const horizontalOffset = 0

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      id='custom-link-editor'
      ref={cardRef}
      className='absolute z-50 w-[320px]'
      style={{
        top:
          variant === 'create' ? position.top - verticalOffset : position.top,
        left: horizontalOffset,
        borderColor: pallete.colors.stroke2
      }}
    >
      <div className='bg-white shadow-xl border rounded-xl p-4'>
        <button
          onClick={onClose}
          className='absolute top-[8px] right-[8px] p-[4px] rounded-full hover:bg-gray-100 transition'
        >
          <Close size={16} color={pallete.colors.textDark6} />
        </button>
        <div className='flex flex-col space-y-[12px]'>
          <div className='mb-[20px]'>
            <Input
              id='link-text'
              labelPosition='top'
              type='text'
              label={
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  className='flex items-center mr-[4px]'
                  color={pallete.colors.textDark3}
                >
                  Text
                </DDSTypography.Paragraph>
              }
              required
              validateInput={false}
              width='260px'
              name='text'
              value={text}
              disabled={!isEditing}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          <div className='mb-[20px]'>
            <Input
              id='link-url'
              value={url}
              labelPosition='top'
              type='text'
              label={
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  className='flex items-center mr-[4px]'
                  color={pallete.colors.textDark3}
                >
                  Link
                </DDSTypography.Paragraph>
              }
              required
              validateInput
              validationRegex={new RegExp(RegexConstants.REFERENCE_REGEX)}
              validate={(value: string) => {
                if (value === '') return { error: false, message: '' }
                if (!RegexConstants.REFERENCE_REGEX.test(value)) {
                  return {
                    error: true,
                    message: 'Please enter a valid link'
                  }
                }
                return { error: false, message: '' }
              }}
              width='260px'
              name='link'
              disabled={!isEditing}
              onChange={(e) => {
                setUrl(e.target.value)
                setAddButtonDisabled(
                  !RegexConstants.REFERENCE_REGEX.test(e.target.value)
                )
              }}
            />
          </div>

          <div className='flex justify-end items-center space-x-[8px]'>
            {isEditing ? (
              <>
                <Button
                  id='cancel'
                  size='small'
                  label={
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.neuButtonText}
                    >
                      Cancel
                    </DDSTypography.Paragraph>
                  }
                  type='ghost'
                  onClick={handleCancel}
                />
                <Button
                  id='apply'
                  size='small'
                  label={
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.textDark1}
                    >
                      Apply
                    </DDSTypography.Paragraph>
                  }
                  type='filled'
                  onClick={handleApply}
                  disabled={!url.trim() || addButtonDisabled}
                />
              </>
            ) : (
              <>
                <Button
                  id='remove'
                  size='small'
                  label={
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.neuButtonText}
                    >
                      Remove
                    </DDSTypography.Paragraph>
                  }
                  type='ghost'
                  onClick={handleRemove}
                />
                <Button
                  id='edit'
                  size='small'
                  label={
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.textDark1}
                    >
                      Edit
                    </DDSTypography.Paragraph>
                  }
                  type='filled'
                  onClick={() => setIsEditing(true)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomLinkEditor
